import { Head, Link, router } from "@inertiajs/react"
import { Check, Pencil, Plus, X } from "lucide-react"
import { useState } from "react"

import { AddHoursDialog } from "@/pages/Team/Employees/AddHoursDialog"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { DataTable, Td, Th, Thead, Tr } from "@/components/steel/table"
import { EditTimeEntryDialog } from "@/components/edit-time-entry-dialog"
import { PageHeader, Section, StatusBadge } from "@/components/steel"
import { TimeFilters } from "@/pages/Team/Time/TimeFilters"

type TimeEntryRow = {
  id: string
  employeeId: string
  employeeName: string
  projectId: string
  projectName: string
  date: string
  hours: number
  description: string
  status: string
  submittedBy: string
}

type Option = { id: string; name: string }

type Props = {
  employees: Option[]
  entries: TimeEntryRow[]
  filters: { employeeId: string | null; projectId: string | null; status: string | null }
  projects: Option[]
  statuses: string[]
}

function formatDate(value: string) {
  const date = new Date(`${value}T00:00:00`)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

export default function TimeIndex({ employees, entries, filters, projects, statuses }: Props) {
  const [editingEntry, setEditingEntry] = useState<TimeEntryRow | null>(null)
  const [logOpen, setLogOpen] = useState(false)
  const [selected, setSelected] = useState<string[]>([])

  const allSelected = entries.length > 0 && selected.length === entries.length
  const pending = entries.filter((e) => e.status === "pending")
  const totalHours = entries.reduce((sum, e) => sum + e.hours, 0)

  const bulkUpdate = (ids: string[], status: string) => {
    if (ids.length === 0) return
    router.patch(
      "/time_entries/bulk",
      { ids, status },
      { preserveScroll: true, onSuccess: () => setSelected([]) },
    )
  }

  return (
    <>
      <Head title="Time" />

      <PageHeader
        title="Time"
        meta={`${entries.length} entries · ${pending.length} pending approval · ${totalHours} hours`}
        actions={
          <>
            {selected.length > 0 ? (
              <>
                <Button variant="outline" size="sm" onClick={() => bulkUpdate(selected, "approved")}>
                  <Check />
                  Approve ({selected.length})
                </Button>
                <Button variant="outline" size="sm" onClick={() => bulkUpdate(selected, "rejected")}>
                  <X />
                  Reject ({selected.length})
                </Button>
              </>
            ) : (
              <Button
                variant="outline"
                size="sm"
                disabled={pending.length === 0}
                onClick={() => bulkUpdate(pending.map((e) => e.id), "approved")}
              >
                <Check />
                Approve all pending
              </Button>
            )}
            <Button size="sm" onClick={() => setLogOpen(true)}>
              <Plus />
              Log time
            </Button>
          </>
        }
      />

      <TimeFilters employees={employees} filters={filters} projects={projects} statuses={statuses} />

      <Section className="pb-7 pt-0">
        {entries.length === 0 ? (
          <p className="border-t border-border py-8 text-center text-label text-muted-foreground">
            No time entries match these filters.
          </p>
        ) : (
          <DataTable>
            <Thead>
              <Th className="w-[26px]">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={() => setSelected(allSelected ? [] : entries.map((e) => e.id))}
                  aria-label="Select all"
                />
              </Th>
              <Th>Date</Th>
              <Th>Employee</Th>
              <Th>Project</Th>
              <Th>Description</Th>
              <Th align="right">Hours</Th>
              <Th>Source</Th>
              <Th>Status</Th>
              <Th />
            </Thead>
            <tbody>
              {entries.map((entry) => (
                <Tr key={entry.id}>
                  <Td className="py-[7px]">
                    <Checkbox
                      checked={selected.includes(entry.id)}
                      onCheckedChange={() =>
                        setSelected((prev) =>
                          prev.includes(entry.id) ? prev.filter((x) => x !== entry.id) : [...prev, entry.id],
                        )
                      }
                      aria-label={`Select ${entry.id}`}
                    />
                  </Td>
                  <Td muted num className="py-[7px]">
                    {formatDate(entry.date)}
                  </Td>
                  <Td strong className="py-[7px]">
                    <Link href={`/team/employees/${entry.employeeId}`} className="hover:underline">
                      {entry.employeeName}
                    </Link>
                  </Td>
                  <Td muted className="py-[7px]">
                    <Link href={`/projects/${entry.projectId}`} className="hover:underline">
                      {entry.projectName.split(" - ")[0]}
                    </Link>
                  </Td>
                  <Td muted className="max-w-[280px] truncate py-[7px]">
                    {entry.description}
                  </Td>
                  <Td align="right" num strong className="py-[7px]">
                    {entry.hours}h
                  </Td>
                  <Td muted className="py-[7px] text-label capitalize">
                    {entry.submittedBy}
                  </Td>
                  <Td className="py-[7px]">
                    <StatusBadge status={entry.status} />
                  </Td>
                  <Td align="right" className="whitespace-nowrap py-[7px]">
                    <Button variant="ghost" size="icon-sm" onClick={() => setEditingEntry(entry)}>
                      <Pencil />
                    </Button>
                    {entry.status === "pending" ? (
                      <>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => bulkUpdate([entry.id], "approved")}
                        >
                          <Check />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => bulkUpdate([entry.id], "rejected")}
                        >
                          <X />
                        </Button>
                      </>
                    ) : null}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </DataTable>
        )}
      </Section>

      <AddHoursDialog
        employees={employees}
        onOpenChange={setLogOpen}
        open={logOpen}
        projects={projects}
      />

      {editingEntry ? (
        <EditTimeEntryDialog
          key={editingEntry.id}
          entry={editingEntry}
          onOpenChange={(open) => !open && setEditingEntry(null)}
          open
        />
      ) : null}
    </>
  )
}
